import { QueryFactory } from '@lmt/shared-types';
import { DataBlockDictionaryDto } from '@lmt/shared-types';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { NS_DataBlockFields } from '../../helpers/localization/useScopedTranslation';
import { useModal } from '../common/ui-kit/modal/useModal';

export function useAddDictionaryHandler(
  onNewDictionaryCreated?: (dictionary: DataBlockDictionaryDto) => void,
) {
  const i18next = useTranslation();
  const dispatch = useDispatch();
  const modals = useModal();
  const [isCreatingNewDictionary, setIsCreatingNewDictionary] = useState(false);
  const queryClient = useQueryClient();

  const addNewDictionaryHandler = useCallback(async () => {
    const newDictionaryName = await modals.showPrompt({
      fieldName: i18next.t('data_blocks.fields.add_dictionary_text'),
      text: '',
      title: i18next.t('data_blocks.fields.add_dictionary_title'),
      defaultValue: '',
      isMandatory: true,
    });
    if (newDictionaryName?.trim()) {
      try {
        setIsCreatingNewDictionary(true);
        // check if dictionary with this localized title exists
        const dictionaries = await QueryFactory.DataBlockDictionaryClient.get();
        const existedDeletedDictionary = dictionaries
          .filter((d) => d.isDeleted)
          ?.find(
            (d) =>
              i18next
                .t(d.title as any, {
                  ns: NS_DataBlockFields,
                })
                .toLowerCase() === newDictionaryName.trim().toLowerCase(),
          );
        const newName = existedDeletedDictionary
          ? existedDeletedDictionary.title
          : newDictionaryName.trim();
        const newDictionaryEntry =
          await QueryFactory.DataBlockDictionaryClient.create({
            title: newName,
          });
        await queryClient.refetchQueries({
          queryKey: QueryFactory.DataBlockDictionaryQuery.getQueryKey(),
        });
        onNewDictionaryCreated?.(newDictionaryEntry);
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      } finally {
        setIsCreatingNewDictionary(false);
      }
    }
  }, [onNewDictionaryCreated]);
  const enumFieldLinkProps = useMemo(
    () => ({
      title: i18next.t('data_blocks.fields.add_dictionary_button'),
      onClick: addNewDictionaryHandler,
    }),
    [addNewDictionaryHandler],
  );

  return useMemo(
    () => ({
      enumFieldLinkProps,
      isCreatingNewDictionary,
    }),
    [enumFieldLinkProps, isCreatingNewDictionary],
  );
}
